// App shell - two entry points:
//   1. Lead flow: availability -> lead form -> thank-you (Julie sends a quote
//      with a signed return link)
//   2. Reservation return: ?r=TOKEN -> reservation form (pre-filled from token)
//      -> thank-you
// The old single-flow (lead -> reservation -> arrival) is replaced. Arrival
// remains a separate URL (?a=TOKEN) sent at PREPARE (-7 days), built later.
// Iframed into WordPress page 65; the height bridge posts the document height
// to the parent so the iframe resizes responsively.
import { useState, useEffect, useLayoutEffect } from "preact/hooks";
import { ensureAnon } from "./firebase.js";
import { reportError } from "./lib/errlog.js";
import { AvailabilityCalendar } from "./components/AvailabilityCalendar.jsx";
import { LeadForm } from "./components/LeadForm.jsx";
import { ThankYou } from "./components/ThankYou.jsx";
import { ReservationReturn } from "./components/ReservationReturn.jsx";
import { ArrivalForm } from "./components/ArrivalForm.jsx";
import { QuoteAdminPanel } from "./components/QuoteAdminPanel.jsx";
import { ErrorBoundary } from "./components/ErrorBoundary.jsx";
import { decodeReservationToken, decodeArrivalToken } from "./lib/token.js";
import { Status } from "./components/Status.jsx";

export function App() {
  const [step, setStep] = useState("availability");
  const [dates, setDates] = useState(null);
  const [pricingBand, setPricingBand] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [thankYouFlow, setThankYouFlow] = useState("lead");
  const [reserveToken, setReserveToken] = useState(null);
  const [arrivalToken, setArrivalToken] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Anonymous session init with retry: a guest on a flaky connection (or a
    // browser that throttles the first request) must self-heal instead of
    // dead-ending on the banner. Only after all retries do we show the error
    // AND report it server-side so the failure is visible in logs.
    let cancelled = false;
    (async () => {
      const delays = [0, 1500, 4000];
      let lastErr = null;
      for (const d of delays) {
        if (d) await new Promise((r) => setTimeout(r, d));
        try {
          await ensureAnon();
          return; // session up
        } catch (err) {
          lastErr = err;
        }
        if (cancelled) return;
      }
      if (!cancelled) {
        reportError("anon_auth_init", lastErr);
        setAuthError("Unable to start a secure session. Please reload.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Path-based route for the quote admin panel: /admin/q/:token. This is a
    // standalone Firebase-Hosted page (not the guest widget); it short-circuits
    // the ?r= / ?a= query-param logic below.
    const path = window.location.pathname;
    if (path.startsWith("/admin/q/")) {
      const token = decodeURIComponent(path.replace("/admin/q/", ""));
      setAdminToken(token);
      setStep("quoteAdmin");
      return;
    }
    const params = new URLSearchParams(window.location.search);
    // Square checkout return: the payment link's redirect_url is the widget
    // root, and Square appends ?transactionId=..&orderId=.. after a successful
    // payment. Show the payment thank-you instead of falling through to the
    // availability calendar (session-48 walkthrough bug).
    if (params.get("transactionId") || params.get("orderId")) {
      setLeadName("");
      setThankYouFlow("payment");
      setStep("thankyou");
      return;
    }
    const r = params.get("r");
    const a = params.get("a");
    if (r) {
      const token = decodeReservationToken(r);
      if (token) {
        setReserveToken(token);
        setStep("reservation");
      } else {
        setStep("expired");
      }
    } else if (a) {
      const token = decodeArrivalToken(a);
      if (token) {
        setArrivalToken(token);
        setStep("arrival");
      } else {
        setStep("expired");
      }
    }
  }, []);

  useLayoutEffect(() => {
    const sendHeight = () => {
      const h = document.documentElement.scrollHeight;
      if (window.parent !== window) {
        window.parent.postMessage({ source: "onebeachbot-widget", height: h }, "https://onebeachroad.com");
      }
    };
    sendHeight();
    window.addEventListener("load", sendHeight);
    return () => window.removeEventListener("load", sendHeight);
  }, [step]);

  const isAdmin = step === "quoteAdmin" || step === "adminDone";

  return (
    <div id="widget-root">
      {!isAdmin ? (
        <header>
          <h1>One Beach Road</h1>
          <p class="sub">Six-bedroom villa - St. Thomas, USVI</p>
        </header>
      ) : null}
      {authError ? <Status kind="err">{authError}</Status> : null}

      {step === "availability" && (
        <AvailabilityCalendar
          onSelectDates={(d) => {
            setDates(d);
            setPricingBand(d.pricing_band);
            setStep("lead");
          }}
        />
      )}

      {step === "lead" && (
        <LeadForm
          initialDates={dates}
          pricingBand={pricingBand}
          rateBands={dates?.rate_bands}
          booked={dates?.booked}
          onDone={(res) => {
            setLeadName(res.form.first_name);
            setThankYouFlow("lead");
            setStep("thankyou");
          }}
        />
      )}

      {step === "thankyou" && (
        <ThankYou name={leadName} flow={thankYouFlow} onReset={() => { setStep("availability"); setDates(null); }} />
      )}

      {step === "reservation" && reserveToken && (
        <ReservationReturn
          token={reserveToken}
          onDone={(res) => { setLeadName(res?.firstName || reserveToken.first_name || ""); setThankYouFlow(res?.flow || "reservation"); setStep("thankyou"); }}
        />
      )}

      {step === "arrival" && arrivalToken && (
        <ArrivalForm
          booking_ref={arrivalToken.booking_id}
          lead_ref={arrivalToken.booking_id}
          email={arrivalToken.email}
          phone_e164={arrivalToken.phone_e164}
          arrival_token={arrivalToken.raw}
          first_name={arrivalToken.first_name}
          last_name={arrivalToken.last_name}
          checkin_date={arrivalToken.checkin_date}
          checkout_date={arrivalToken.checkout_date}
          adults={arrivalToken.adults}
          children={arrivalToken.children}
          onDone={() => { setLeadName(arrivalToken.first_name); setThankYouFlow("arrival"); setStep("thankyou"); }}
        />
      )}

      {step === "expired" && (
        <div class="surface">
          <h2>This link has expired</h2>
          <p>If Julie sent you this link recently, reach out to her and she'll send a fresh one. Call or text <strong>507-693-7825</strong>.</p>
        </div>
      )}

      {step === "quoteAdmin" && adminToken && (
        <ErrorBoundary context="quote_admin_render">
          <QuoteAdminPanel token={adminToken} onDone={() => setStep("adminDone")} />
        </ErrorBoundary>
      )}

      {step === "adminDone" && (
        <div class="surface">
          <h2>Quote sent</h2>
          <p>The guest will receive their quote email shortly.</p>
        </div>
      )}
    </div>
  );
}
